import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ShieldCheck, MapPin, User, Mail, Phone, Package, CheckCircle2, Crown, ArrowLeft, CreditCard, Truck } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { CartItem, Page } from '../App';
const PAYSTACK_PUBLIC_KEY = 'pk_test_1087d66471ce6e13f4b544d715af46c5ad9f7a85';
const NIGERIAN_STATES = ['Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT - Abuja', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'];
interface PageProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onNavigate: (page: Page) => void;
  cartItems: CartItem[];
  cartCount: number;
  isCartOpen: boolean;
  onOpenCart: () => void;
  onCloseCart: () => void;
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
}
declare global {
  interface Window {
    PaystackPop: {
      setup: (config: {
        key: string;
        email: string;
        amount: number;
        currency: string;
        ref: string;
        metadata?: object;
        callback: (response: {
          reference: string;
        }) => void;
        onClose: () => void;
      }) => {
        openIframe: () => void;
      };
    };
  }
}
export function CheckoutPage({
  onNavigate,
  cartItems,
  cartCount,
  isCartOpen,
  onOpenCart,
  onCloseCart,
  onUpdateQuantity,
  onRemoveItem
}: PageProps) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    notes: ''
  });
  const [paystackReady, setPaystackReady] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    if (document.getElementById('paystack-script')) {
      setPaystackReady(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'paystack-script';
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.onload = () => setPaystackReady(true);
    document.body.appendChild(script);
  }, []);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0
  }).format(amount);
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const isBeninCity = form.state === 'Edo';
  const shippingFee = cartItems.length === 0 ? 0 : isBeninCity ? 6000 : 12000;
  const total = subtotal + shippingFee;
  const isFormValid = form.firstName && form.lastName && form.email && form.phone && form.address && form.city && form.state;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handlePayment = () => {
    if (!paystackReady || !isFormValid || cartItems.length === 0) return;
    setIsProcessing(true);
    const ref = 'LXC-' + Math.floor(100000 + Math.random() * 900000);
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: form.email,
      amount: total * 100,
      currency: 'NGN',
      ref,
      metadata: {
        custom_fields: [{
          display_name: 'Customer Name',
          variable_name: 'customer_name',
          value: `${form.firstName} ${form.lastName}`
        }, {
          display_name: 'Delivery Address',
          variable_name: 'delivery_address',
          value: `${form.address}, ${form.city}, ${form.state}`
        }]
      },
      callback: response => {
        setIsProcessing(false);
        setOrderSuccess(response.reference);
      },
      onClose: () => {
        setIsProcessing(false);
      }
    });
    handler.openIframe();
  };
  if (orderSuccess) {
    return <div className="bg-[#0F0F0F] min-h-screen text-white flex flex-col" data-id="element-316">
        <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-317" />
        <div className="flex-1 flex items-center justify-center px-4 py-20" data-id="element-318">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="max-w-md w-full text-center" data-id="element-319">
            <div className="w-20 h-20 bg-[#00FF87]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-320">
              <CheckCircle2 className="w-10 h-10 text-[#00FF87]" data-id="element-321" />
            </div>
            <h2 className="text-3xl font-bold font-serif text-white mb-3" data-id="element-322">
              Order Confirmed!
            </h2>
            <p className="text-gray-400 mb-4" data-id="element-323">
              Thank you, {form.firstName}. Your payment was successful.
            </p>
            <div className="glass-panel rounded-xl p-4 border border-[#D4AF37]/20 mb-6" data-id="element-324">
              <p className="text-gray-500 text-xs uppercase tracking-wider mb-1" data-id="element-325">
                Order Reference
              </p>
              <p className="text-[#D4AF37] font-bold text-lg font-mono" data-id="element-326">
                {orderSuccess}
              </p>
            </div>
            <p className="text-gray-500 text-sm mb-8" data-id="element-327">
              A confirmation email will be sent to{' '}
              <span className="text-white" data-id="element-328">{form.email}</span>.<br data-id="element-329" />
              Delivery to {form.city}, {form.state} within 2–5 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-330">
              <button onClick={() => onNavigate('track-order')} className="px-6 py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#c4a030] transition-colors" data-id="element-331">
                Track Your Order
              </button>
              <button onClick={() => onNavigate('home')} className="px-6 py-3 border border-white/20 text-white rounded-full hover:bg-white/5 transition-colors" data-id="element-332">
                Continue Shopping
              </button>
            </div>
          </motion.div>
        </div>
        <Footer onNavigate={onNavigate} data-id="element-333" />
      </div>;
  }
  return <div className="bg-[#0F0F0F] min-h-screen text-white" data-id="element-334">
      <Navbar onOpenCart={onOpenCart} cartCount={cartCount} onNavigate={onNavigate} data-id="element-335" />

      <div className="pt-28 pb-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-336">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-8" data-id="element-337">
          <button onClick={() => onNavigate('home')} className="hover:text-[#D4AF37] transition-colors" data-id="element-338">
            Home
          </button>
          <ChevronRight className="w-4 h-4" data-id="element-339" />
          <span className="text-[#D4AF37]" data-id="element-340">Checkout</span>
        </div>

        <div className="flex items-center gap-3 mb-10" data-id="element-341">
          <Crown className="w-6 h-6 text-[#D4AF37]" data-id="element-342" />
          <h1 className="text-3xl font-bold font-serif text-white" data-id="element-343">
            Secure Checkout
          </h1>
          <div className="flex items-center gap-1 ml-auto text-[#00FF87] text-sm" data-id="element-344">
            <ShieldCheck className="w-4 h-4" data-id="element-345" />
            <span className="hidden sm:inline" data-id="element-346">Paystack Secured</span>
          </div>
        </div>

        {cartItems.length === 0 ? <div className="text-center py-20" data-id="element-347">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" data-id="element-348" />
            <p className="text-gray-400 text-lg mb-6" data-id="element-349">Your cart is empty</p>
            <button onClick={() => onNavigate('home')} className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-bold rounded-full hover:bg-[#c4a030] transition-colors" data-id="element-350">
              <ArrowLeft className="w-4 h-4" data-id="element-351" />
              Continue Shopping
            </button>
          </div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-352">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6" data-id="element-353">
              {/* Customer Details */}
              <div className="glass-panel rounded-2xl p-6 border border-white/10" data-id="element-354">
                <h2 className="text-lg font-semibold text-white mb-5 flex items-center gap-2" data-id="element-355">
                  <User className="w-5 h-5 text-[#D4AF37]" data-id="element-356" />
                  Customer Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-357">
                  <div data-id="element-358">
                    <label className="block text-sm text-gray-400 mb-1" data-id="element-359">
                      First Name
                    </label>
                    <input type="text" name="firstName" placeholder="Chidi" value={form.firstName} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-360" />
                  </div>
                  <div data-id="element-361">
                    <label className="block text-sm text-gray-400 mb-1" data-id="element-362">
                      Last Name
                    </label>
                    <input type="text" name="lastName" placeholder="Okonkwo" value={form.lastName} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-363" />
                  </div>
                  <div data-id="element-364">
                    <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1" data-id="element-365">
                      <Mail className="w-3 h-3" data-id="element-366" /> Email
                    </label>
                    <input type="email" name="email" placeholder="chidi@example.com" value={form.email} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-367" />
                  </div>
                  <div data-id="element-368">
                    <label className="block text-sm text-gray-400 mb-1 flex items-center gap-1" data-id="element-369">
                      <Phone className="w-3 h-3" data-id="element-370" /> Phone
                    </label>
                    <input type="tel" name="phone" placeholder="+234 800 000 0000" value={form.phone} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-371" />
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              <div className="glass-panel rounded-2xl p-6 border border-white/10" data-id="element-372">
                <h2 className="text-lg font-semibold text-white mb-5 flex items-center gap-2" data-id="element-373">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" data-id="element-374" />
                  Delivery Address
                </h2>
                <div className="space-y-4" data-id="element-375">
                  <div data-id="element-376">
                    <label className="block text-sm text-gray-400 mb-1" data-id="element-377">
                      Street Address
                    </label>
                    <input type="text" name="address" placeholder="12 Ring Road, GRA" value={form.address} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-378" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-379">
                    <div data-id="element-380">
                      <label className="block text-sm text-gray-400 mb-1" data-id="element-381">
                        City / LGA
                      </label>
                      <input type="text" name="city" placeholder="Benin City" value={form.city} onChange={handleChange} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-382" />
                    </div>
                    <div data-id="element-383">
                      <label className="block text-sm text-gray-400 mb-1" data-id="element-384">
                        State
                      </label>
                      <select name="state" value={form.state} onChange={handleChange} className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#D4AF37] transition-colors" data-id="element-385">
                        <option value="" disabled data-id="element-386">
                          Select State
                        </option>
                        {NIGERIAN_STATES.map(s => <option key={s} value={s} data-id="element-387">
                            {s}
                          </option>)}
                      </select>
                    </div>
                  </div>
                  <AnimatePresence data-id="element-388">
                    {form.state && <motion.div initial={{
                  opacity: 0,
                  y: -5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0
                }} className={`p-3 rounded-lg text-sm flex items-center gap-2 ${isBeninCity ? 'bg-[#00FF87]/10 border border-[#00FF87]/20 text-[#00FF87]' : 'bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37]'}`} data-id="element-389">
                        <Truck className="w-4 h-4 flex-shrink-0" data-id="element-390" />
                        {isBeninCity ? 'Intra-city delivery (Benin City): ₦6,000 flat rate' : `Inter-state delivery to ${form.state}: ₦12,000`}
                      </motion.div>}
                  </AnimatePresence>
                  <div data-id="element-391">
                    <label className="block text-sm text-gray-400 mb-1" data-id="element-392">
                      Order Notes (optional)
                    </label>
                    <textarea name="notes" placeholder="Any special delivery instructions..." value={form.notes} onChange={handleChange} rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37] transition-colors resize-none" data-id="element-393" />
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="space-y-4" data-id="element-394">
              <div className="glass-panel rounded-2xl p-6 border border-white/10 lg:sticky lg:top-24" data-id="element-395">
                <h2 className="text-lg font-semibold text-white mb-5" data-id="element-396">
                  Order Summary
                </h2>
                <div className="space-y-3 mb-5 max-h-64 overflow-y-auto pr-1" data-id="element-397">
                  {cartItems.map(item => <div key={item.id} className="flex gap-3" data-id="element-398">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0" data-id="element-399">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-400" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-401">
                        <p className="text-white text-sm font-medium line-clamp-1" data-id="element-402">
                          {item.name}
                        </p>
                        <p className="text-gray-500 text-xs" data-id="element-403">
                          Qty: {item.quantity}
                        </p>
                      </div>
                      <p className="text-white text-sm font-medium flex-shrink-0" data-id="element-404">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>)}
                </div>
                <div className="border-t border-white/10 pt-4 space-y-2 mb-5" data-id="element-405">
                  <div className="flex justify-between text-gray-400 text-sm" data-id="element-406">
                    <span data-id="element-407">Subtotal</span>
                    <span data-id="element-408">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400 text-sm" data-id="element-409">
                    <span data-id="element-410">Shipping</span>
                    <span className={form.state ? 'text-white' : 'text-gray-600'} data-id="element-411">
                      {form.state ? formatCurrency(shippingFee) : 'Select state'}
                    </span>
                  </div>
                  <div className="flex justify-between text-white font-bold text-lg pt-2 border-t border-white/10" data-id="element-412">
                    <span data-id="element-413">Total</span>
                    <span className="text-[#D4AF37]" data-id="element-414">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>
                <button onClick={handlePayment} disabled={!isFormValid || !paystackReady || isProcessing} className="w-full flex items-center justify-center gap-2 py-4 bg-[#D4AF37] text-black font-bold rounded-xl hover:bg-[#c4a030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-415">
                  {isProcessing ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-416" /> : <>
                      <CreditCard className="w-5 h-5" data-id="element-417" />
                      Pay {formatCurrency(total)}
                    </>}
                </button>
                <div className="mt-4 flex items-center justify-center gap-2 text-gray-600 text-xs" data-id="element-418">
                  <ShieldCheck className="w-3 h-3" data-id="element-419" />
                  Secured by Paystack · 256-bit SSL
                </div>
              </div>
              <button onClick={() => onNavigate('home')} className="w-full flex items-center justify-center gap-2 py-3 border border-white/10 text-gray-400 hover:text-white hover:border-white/20 rounded-xl transition-colors text-sm" data-id="element-420">
                <ArrowLeft className="w-4 h-4" data-id="element-421" />
                Continue Shopping
              </button>
            </div>
          </div>}
      </div>

      <Footer onNavigate={onNavigate} data-id="element-422" />
    </div>;
}